import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, TextField, CircularProgress,
  Stack, MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { DogProfileExtended } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  dog: DogProfileExtended | null;
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

const SEX_OPTIONS = ["Male", "Female"];
const REPRODUCTION_OPTIONS = ["Neutered", "Spayed", "Not neutered", "Not spayed"];
const BODY_CONDITION_OPTIONS = ["Underweight", "Ideal", "Overweight"];
const ACTIVITY_OPTIONS = ["0-45 minutes/day", "45-90 minutes/day", "90+ minutes/day"];
const TREAT_FREQUENCY_OPTIONS = ["Heavy Snacker", "Occasional", "Never"];

export function EditDogModal({ open, onClose, dog, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: "",
    breed: "",
    sex: "",
    reproduction: "",
    ageYears: 0,
    ageMonths: 0,
    weightKg: 0,
    bodyCondition: "",
    activityLevel: "",
    allergies: "",
    healthIssues: "",
    treatFrequency: "",
  });

  useEffect(() => {
    if (open && dog) {
      setForm({
        name: dog.name || "",
        breed: dog.breed || "",
        sex: (dog as any).sex || "",
        reproduction: (dog as any).reproduction || "",
        ageYears: (dog as any).ageYears || 0,
        ageMonths: (dog as any).ageMonths || 0,
        weightKg: dog.weightKg || 0,
        bodyCondition: (dog as any).bodyCondition || "",
        activityLevel: (dog as any).activityLevel || "",
        allergies: Array.isArray((dog as any).allergies) ? (dog as any).allergies.join(", ") : "",
        healthIssues: Array.isArray((dog as any).healthIssues) ? (dog as any).healthIssues.join(", ") : "",
        treatFrequency: (dog as any).treatFrequency || "",
      });
      setError(null);
    }
  }, [open, dog]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleNumberChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value) || 0;
    setForm(prev => ({ ...prev, [field]: val }));
  };

  const handleSave = async () => {
    if (!dog) return;
    setLoading(true);
    setError(null);

    try {
      const allergies = form.allergies
        ? form.allergies.split(",").map(s => s.trim()).filter(Boolean)
        : [];
      const healthIssues = form.healthIssues
        ? form.healthIssues.split(",").map(s => s.trim()).filter(Boolean)
        : [];

      const res = await call("/account/dogs/update", {
        method: "POST",
        body: {
          dogId: dog.id,
          name: form.name,
          breed: form.breed,
          sex: form.sex || undefined,
          reproduction: form.reproduction || undefined,
          ageYears: form.ageYears,
          ageMonths: form.ageMonths,
          weightKg: form.weightKg,
          bodyCondition: form.bodyCondition || undefined,
          activityLevel: form.activityLevel || undefined,
          allergies,
          healthIssues,
          treatFrequency: form.treatFrequency || undefined,
        },
      });
      if (!res.ok) throw new Error(res.error || "Failed to update dog");
      onSuccess(`${form.name || "Dog"} updated successfully!`);
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to update dog");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800, color: COLORS.brownDeep }}>Edit Dog Profile</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="Name" value={form.name} onChange={handleChange("name")} size="small" required />
            <TextField fullWidth label="Breed" value={form.breed} onChange={handleChange("breed")} size="small" />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth select label="Sex" value={form.sex} onChange={handleChange("sex")} size="small"
            >
              <MenuItem value="">-</MenuItem>
              {SEX_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <TextField
              fullWidth select label="Reproduction" value={form.reproduction} onChange={handleChange("reproduction")} size="small"
            >
              <MenuItem value="">-</MenuItem>
              {REPRODUCTION_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth label="Age (years)" type="number" value={form.ageYears}
              onChange={handleNumberChange("ageYears")} size="small"
              inputProps={{ min: 0, max: 25 }}
            />
            <TextField
              fullWidth label="Age (months)" type="number" value={form.ageMonths}
              onChange={handleNumberChange("ageMonths")} size="small"
              inputProps={{ min: 0, max: 11 }}
            />
          </Stack>

          <TextField
            fullWidth label="Weight (kg)" type="number" value={form.weightKg}
            onChange={handleNumberChange("weightKg")} size="small"
            inputProps={{ min: 0.1, max: 100, step: 0.1 }}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth select label="Body Condition" value={form.bodyCondition}
              onChange={handleChange("bodyCondition")} size="small"
            >
              <MenuItem value="">-</MenuItem>
              {BODY_CONDITION_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <TextField
              fullWidth select label="Activity Level" value={form.activityLevel}
              onChange={handleChange("activityLevel")} size="small"
            >
              <MenuItem value="">-</MenuItem>
              {ACTIVITY_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Stack>

          <TextField
            fullWidth label="Allergies (comma separated)" value={form.allergies}
            onChange={handleChange("allergies")} size="small"
            placeholder="e.g., Chicken, Grain"
          />

          <TextField
            fullWidth label="Health Issues (comma separated)" value={form.healthIssues}
            onChange={handleChange("healthIssues")} size="small"
            placeholder="e.g., Sensitive stomach, Joint issues"
          />

          <TextField
            fullWidth select label="Treat Frequency" value={form.treatFrequency}
            onChange={handleChange("treatFrequency")} size="small"
          >
            <MenuItem value="">-</MenuItem>
            {TREAT_FREQUENCY_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !form.name}
          sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none" }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save Dog"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
